import { Button, Group, Box, useMantineTheme, Text } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import {
  useDeleteEmployeeRoleMutation,
  useGetEmployeeRoleNamesQuery,
  useUpdateRoleAssignmentMutation,
} from "src/generated/graphql";
import { memo, useRef, useState } from "react";
import { object, string } from "yup";
import Select, { ISelect } from "src/components/Select";
import { closeAllModals } from "@mantine/modals";
import { useAppContext } from "src/api/AppContext";

type RoleFormProps = {
  setrefetchTableData: (bool: boolean) => void;
  roleId: string;
  refetchTableData: boolean;
};
type IFormProps = {
  newRoleId: string;
};

const EmployeeRoleFormSchema = object().shape({
  newRoleId: string().required("Role is required"),
});

const RoleForm = ({
  setrefetchTableData,
  roleId,
  refetchTableData,
}: RoleFormProps) => {
  const theme = useMantineTheme();
  const [rolesData, setrolesData] = useState<ISelect[]>([]);
  const isRefetch = useRef(refetchTableData);
  const { currentEmployee, getEmployee } = useAppContext();

  const form = useForm<IFormProps>({
    initialValues: {
      newRoleId: "",
    },
    validate: yupResolver(EmployeeRoleFormSchema),
  });

  useGetEmployeeRoleNamesQuery({
    skip: !roleId,
    onCompleted(data) {
      if (!data.employeeRoleMany) return;
      setrolesData(
        data.employeeRoleMany
          .filter((r) => r.id !== roleId!)
          .map((e) => ({
            value: e.id,
            label: e.name,
          }))
      );
    },
    fetchPolicy: "network-only",
  });

  const [deleteEmployeeRoleEntry, { loading: delLoading }] =
    useDeleteEmployeeRoleMutation();

  const [updateRoleAssignment, { loading }] = useUpdateRoleAssignmentMutation({
    onCompleted: () => {
      // If deleted role is assigned to currentEmployee Then fetch getEmployee updates
      if (currentEmployee?.role?.id === roleId) {
        getEmployee();
      }

      isRefetch.current = !isRefetch.current;
      setrefetchTableData(isRefetch.current);
      form.resetDirty();
      closeAllModals();
    },
  });

  const handleSubmit = async (values: IFormProps) => {
    await deleteEmployeeRoleEntry({
      variables: { deleteEmployeeRoleId: roleId },
    });
    await updateRoleAssignment({
      variables: { roleId, newRoleId: values.newRoleId },
    });
  };

  return (
    <Box sx={{ maxWidth: 360 }} mx="auto">
      <Text size={14}>
        This role is already assigned to some employees, please select a new
        role to assign them or cancel your removal request.
      </Text>
      <form
        onSubmit={form.onSubmit((values) => {
          handleSubmit(values);
        })}
      >
        <Select
          data={rolesData}
          label={`Select Role`}
          {...form.getInputProps("newRoleId")}
        />

        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={() => closeAllModals()}
            disabled={loading || delLoading}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            radius="md"
            type="submit"
            disabled={loading || delLoading}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            loading={loading || delLoading}
          >
            Submit
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default memo(RoleForm);
